import {get, postCatching} from '@/bank/bankApi.service'
import {computed, onMounted, onUnmounted, readonly, ref} from 'vue'
import {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED} from '@/types/events'
import {
    CancelOnboardingCommand,
    CheckExistingOnboardingCommand,
    CheckOnboardingStatusCommand,
    InitiateOnboardingCommand,
    Onboarding,
    OnboardingApiVersion,
    OnboardingStatus
} from '@/bank/onboarding/onboarding.types'
import {selectedOrg} from '@/tenants/org.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {HttpStatusCode} from 'axios'

const onboarding = ref<Onboarding>()
const isInitialized = ref(false)

const isOnboarded = computed(() => {
    if (!onboarding.value) return false
    return onboarding.value.onboardingStatus === OnboardingStatus.Accepted
})

const isCancellable = computed(() => {
    if (!onboarding.value) return false
    if (onboarding.value.apiVersion !== OnboardingApiVersion.v2) return false
    return !([OnboardingStatus.Accepted, OnboardingStatus.Rejected, OnboardingStatus.Cancelled].includes(onboarding.value.onboardingStatus))
})

const isProcessing = computed(() => {
    if (!onboarding.value) return false
    return [OnboardingStatus.Created, OnboardingStatus.Processing, OnboardingStatus.AwaitingCustomerAction, OnboardingStatus.ManualHandling].includes(onboarding.value.onboardingStatus)
})

const canInitiateOnboarding = computed(() => {
    if (!onboarding.value) return true
    return [OnboardingStatus.Cancelled, OnboardingStatus.Rejected].includes(onboarding.value.onboardingStatus)
})

export const useOnboarding = () => {
    const {onEvents, cleanUp} = useSingletonEmitter('banking:onboarding-service')

    const getUserOnboardings = async () => await get('/users/self/onboarding')

    // TODO: We should move towards dummy url for redirect as the user will be able to "return to conta" on the original redirectUrl whenever they open the ztl form / status page
    const initiateOnboarding = async () => {
        const result = await postCatching<Onboarding>('/onboarding/:initiate', {tenantId: selectedOrg.value?.id, redirectUrl: `${window.location}`} as InitiateOnboardingCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke initiere onboarding', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            onboarding.value = result.value
        }
    }

    const checkOnboardingStatus = async () => {
        if (!isProcessing.value) return
        const result = await postCatching<Onboarding>('/onboarding/-/:check-status', {tenantId: selectedOrg.value?.id, onboardingUuid: onboarding.value?.uuid} as CheckOnboardingStatusCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke sjekke onboarding status', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            onboarding.value = result.value
        }
    }

    const checkExistingOnboarding = async () => {
        const result = await postCatching<Onboarding>('/onboarding/:check-existing-onboarding', {tenantId: selectedOrg.value?.id} as CheckExistingOnboardingCommand)
        if (result.isLeft()) {
            if (result.error.status !== HttpStatusCode.NotFound) {
                notifyError({title: 'Feilet under sjekk av onboarding', description: {descriptionMessage: result.error.detail, error: result.error}})
            }
            onboarding.value = undefined
        } else {
            onboarding.value = result.value
        }
    }

    const cancelOnboarding = async () => {
        if (!isCancellable.value) return
        const result = await postCatching<Onboarding>('/onboarding/-/:cancel', {tenantId: selectedOrg.value?.id, onboardingUuid: onboarding.value?.uuid} as CancelOnboardingCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke avbryte onboarding', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            onboarding.value = result.value
        }
    }

    if (!isInitialized.value) {
        isInitialized.value = true
        checkExistingOnboarding()
    }

    onMounted(async () => {
        onEvents([ORG_SELECTED], checkExistingOnboarding)
        onEvents([NO_ORG_SELECTED, LOGOUT], () => {
            onboarding.value = undefined
            isInitialized.value = false
        })
    })

    onUnmounted(() => {
        cleanUp()
    })

    return {
        onboarding: readonly(onboarding),
        getUserOnboardings,
        checkOnboardingStatus,
        initiateOnboarding,
        checkExistingOnboarding,
        cancelOnboarding,
        isOnboarded,
        isCancellable,
        isProcessing,
        canInitiateOnboarding
    }
}
